import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '@/hooks/reduxHooks';
import { createAgentClient, type AgentClient } from './agentClient';
import { readAgentApiConfig, type AgentApiConfig } from './config';
import { loadMockTransport, type MockTransport } from './mockTransport';

/**
 * The agent-service client for the signed-in user, shared by the chat drawer
 * and the Agents page. `client` is null while the mode is 'off' or the dev mock
 * is still loading. The JWT is read through a ref, so a token refresh never
 * rebuilds the client.
 */
export function useAgentClient(): {
  client: AgentClient | null;
  mode: AgentApiConfig['mode'];
} {
  const token = useAppSelector((s) => s.auth.token);
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const config = useMemo(
    () =>
      readAgentApiConfig(
        import.meta.env.VITE_AGENT_API_URL,
        import.meta.env.DEV,
      ),
    [],
  );

  // Dev only: the scripted service is loaded once per page load and kept, so
  // its run ids and sessions survive across turns. It never loads for 'real'
  // or 'off', and never in a production bundle (see mockTransport.ts).
  const [mock, setMock] = useState<MockTransport | null>(null);
  useEffect(() => {
    if (config.mode !== 'mock') return;
    let cancelled = false;
    loadMockTransport(config.mode)
      .then((transport) => {
        if (!cancelled) setMock(transport);
      })
      .catch(() => undefined); // dev tooling: the panel just stays idle
    return () => {
      cancelled = true;
    };
  }, [config.mode]);

  const client = useMemo(() => {
    const getToken = () => tokenRef.current;
    if (config.mode === 'real') {
      return createAgentClient({ baseUrl: config.baseUrl, getToken });
    }
    if (config.mode === 'mock' && mock) {
      return createAgentClient({
        baseUrl: mock.baseUrl,
        getToken,
        fetchImpl: mock.fetchImpl,
      });
    }
    return null;
  }, [config, mock]);

  return { client, mode: config.mode };
}
